import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { ChevronRight } from "lucide-react";
import type { IssuePerson, IssueReport, IssueStatus } from "../../types/issue-triage";
import { IssueCard } from "./IssueCard";

interface KanbanColumnProps {
  id: string;
  title: string;
  statuses: IssueStatus[];
  issues: IssueReport[];
  count: number;
  onIssueClick: (issue: IssueReport) => void;
  onApprove?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onCopy?: (id: string) => void;
  /** Assignable developers; with onOwnerChange, each card shows an owner picker. */
  owners?: IssuePerson[];
  onOwnerChange?: (id: string, owner: IssuePerson | null) => void;
  onMove?: (issue: IssueReport, status: IssueStatus) => void;
  color?: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function KanbanColumn({
  id,
  title,
  issues,
  count,
  onIssueClick,
  onApprove,
  onDismiss,
  onCopy,
  owners,
  onOwnerChange,
  onMove,
  color = "gray",
  collapsed,
  onToggleCollapse,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  const colorClasses: Record<string, string> = {
    amber: "bg-amber-50 border-amber-200",
    blue: "bg-blue-50 border-blue-200",
    purple: "bg-purple-50 border-purple-200",
    green: "bg-green-50 border-green-200",
    gray: "bg-slate-50 border-slate-200",
    slate: "bg-white border-slate-300",
  };

  const headerColors: Record<string, string> = {
    amber: "text-amber-700",
    blue: "text-blue-700",
    purple: "text-purple-700",
    green: "text-green-700",
    gray: "text-slate-700",
    slate: "text-slate-800",
  };

  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        className={`flex flex-col w-12 rounded-lg border ${colorClasses[color]} h-full cursor-pointer transition-all ${
          isOver ? "ring-2 ring-blue-400 ring-offset-2" : ""
        }`}
        onClick={onToggleCollapse}
        title="Expand Done column"
      >
        <div className="p-2 flex flex-col items-center gap-2">
          <span className="px-2 py-0.5 text-xs font-medium bg-white rounded-full text-slate-600 border border-slate-200">
            {count}
          </span>
          <span className={`text-xs font-medium ${headerColors[color]} [writing-mode:vertical-lr] rotate-180`}>
            {title}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[300px] max-w-[350px] rounded-lg border ${colorClasses[color]} h-full transition-all ${isOver ? "ring-2 ring-blue-400 ring-offset-2 scale-[1.01]" : ""}`}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-inherit">
        <div className="flex items-center justify-between">
          <h3 className={`font-medium ${headerColors[color]}`}>{title}</h3>
          <div className="flex items-center gap-1">
            <span className="px-2 py-0.5 text-xs font-medium bg-white rounded-full text-slate-600 border border-slate-200">
              {count}
            </span>
            {onToggleCollapse && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                title="Collapse column"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {issues.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            No issues
          </div>
        ) : (
          issues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              onClick={() => onIssueClick(issue)}
              onApprove={onApprove}
              onDismiss={onDismiss}
              onCopy={onCopy}
              owners={owners}
              onOwnerChange={onOwnerChange}
              onMove={onMove}
            />
          ))
        )}
      </div>
    </div>
  );
}
